import Layout from "@/components/layout/Layout";
import Button from "@/components/ui/Button";

import TextField from "@/components/ui/Textfield";
import React, { useEffect, useState } from "react";
import { BiSearch } from "react-icons/bi";
import { useShallow } from "zustand/react/shallow";
import useManageMediaAgencyStore from "./store/mediaAgency";
import Spinner from "@/components/ui/Spinner";
import useGlobalStore from "@/store/globalStore";

export const ManageMediaAgency: React.FC = () => {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [isLoading, setIsLoading] = useState(false);

  const { fetchAgency, agency, sortField } = useManageMediaAgencyStore(
    useShallow((state) => ({
      fetchAgency: state.fetchAgency,
      agency: state.agency,
      sortField: state.sortField,
    }))
  );

  const setError = useGlobalStore((state) => state.setError);

  const fetchData = async (page?: number, searchQuery?: string, sort?: string) => {
    setIsLoading(true);
    try {
      const pageToFetch = page ?? currentPage;
      const query = searchQuery !== undefined ? searchQuery : search;
      const sortValue = sort !== undefined ? sort : sortField;
      await fetchAgency(pageToFetch.toString(), query, sortValue || undefined);
    } catch (error) {
      setError(error instanceof Error ? error.message : "Unknown error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
    fetchData(1, value);
  };

  const goPrevious = async () => {
    const newPage = Math.max(currentPage - 1, 1);
    setCurrentPage(newPage);
    await fetchData(newPage);
  };

  const goNext = async () => {
    const lastPage = agency.meta?.last_page || 1;
    const newPage = Math.min(currentPage + 1, lastPage);
    setCurrentPage(newPage);
    await fetchData(newPage);
  };

  const handleSort = (field: string) => {
    let newSort: string;
    if (sortField === field) {
      newSort = `-${field}`;
    } else if (sortField === `-${field}`) {
      newSort = '';
    } else {
      newSort = field;
    }
    setCurrentPage(1);
    fetchData(1, undefined, newSort);
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField === field) {
      return (
        <svg className="w-4 h-4 text-pink-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M6 15l6-6 6 6" />
        </svg>
      );
    }
    if (sortField === `-${field}`) {
      return (
        <svg className="w-4 h-4 text-pink-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M6 9l6 6 6-6" />
        </svg>
      );
    }
    return null;
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <Layout>
      <div>
        <h1 className="text-2xl font-bold mb-8">Media Agency</h1>

        <div className="flex justify-between mb-6">
          <div>
            <TextField
              type="text"
              leadingIcon={<BiSearch />}
              placeholder="Search by keyword, name, etc."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-96"
            />
          </div>
        </div>

        <div className="grid grid-cols-12 border-b border-gray-300 pb-2 mb-2 text-sm text-gray-700 select-none">
          <div className="col-span-1 flex items-center gap-1">
            Logo
          </div>
          <div className="col-span-3 flex items-center cursor-pointer gap-1" onClick={() => handleSort('name')}>
            Name
            <SortIcon field="name" />
          </div>
          <div className="col-span-4 flex items-center cursor-pointer gap-1" onClick={() => handleSort('industry')}>
            Industry
            <SortIcon field="industry" />
          </div>
          <div className="col-span-2 flex items-center cursor-pointer gap-1" onClick={() => handleSort('size')}>
            Size
            <SortIcon field="size" />
          </div>
          <div className="col-span-2 flex items-center cursor-pointer gap-1" onClick={() => handleSort('phone')}>
            Phone
            <SortIcon field="phone" />
          </div>
        </div>

        <div className="divide-y divide-gray-300">
          {agency == null ? (
            <div className="flex justify-center mt-10">
              <Spinner />
            </div>
          ) : (
            agency.data.map((ag) => {
              return (
                <div
                  key={ag.id}
                  className="grid grid-cols-12 items-center py-3 text-sm"
                >
                  <div className="col-span-1">
                    {ag.logo_url_signed || ag.logo_url ? (
                      <img
                        src={ag.logo_url_signed || ag.logo_url}
                        alt={`${ag.name} logo`}
                        className="w-12 h-12 object-contain rounded"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                        <span className="text-xs text-gray-500">
                          {ag.name?.charAt(0).toUpperCase() || 'N'}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="col-span-3 flex items-center gap-3">
                    <div>{ag.name}</div>
                  </div>
                  <div className="col-span-4">{ag.industry || '-'}</div>
                  <div className="col-span-2">{ag.size || '-'}</div>
                  <div className="col-span-2">{ag.phone || '-'}</div>
                </div>
              );
            })
          )}
        </div>

        {agency !== null && (
          <div className="flex justify-end items-center mt-6 space-x-4 text-sm">
            <Button
              type="secondary"
              onClick={goPrevious}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <span>
              Page {currentPage} of {agency.meta.last_page || 1}
            </span>
            <Button
              type="secondary"
              onClick={goNext}
              disabled={
                currentPage === agency.meta.last_page ||
                agency.meta.last_page === 0
              }
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ManageMediaAgency;
